/* eslint-disable @typescript-eslint/no-explicit-any */

import React, { useEffect } from 'react'
import { useForm, SubmitHandler } from 'react-hook-form'
import { useParams } from 'react-router-dom'

import { Input, InputGroup } from '@components'
import Button from '@components/Button'
import Modal from '@components/Modal'
import { successMessages } from '@constants/feedbackMessages'
import { Dialog } from '@headlessui/react'
import { yupResolver } from '@hookform/resolvers/yup'
import { UpdateSubjectRequest } from '@models/discipline.model'
import { addSubjectsSchema } from '@schemas/addSubject.schema'
import { disciplineApi } from '@services/DisciplineApi'
import { closeModal } from '@store/ducks/modal'
import { notificationCreators } from '@store/ducks/notification'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { inputIsValid } from '@utils/helpers'
import { getCssStyle, pixelsToRem } from '@utils/helpers/styleHelpers'

type Inputs = UpdateSubjectRequest

const styles = {
	container:
		'relative inline-block align-bottom bg-white rounded-lg pl-10 pr-10 pt-6 pb-9 text-left overflow-hidden shadow-xl transform transition-all my-8 sm:align-middle',
	title: 'text-lg leading-6 font-medium text-gray-900',
	subTitle: 'text-gray-500 mt-1',
	buttonGroup: 'mt-7 flex gap-4 justify-end',
}

export default function EditSubject() {
	const { id: disciplineId } = useParams()

	const { modalInfo: id, modalName } = useAppSelector((state) => state.modal)

	const dispatch = useAppDispatch()

	const [getSubject, { data: subject }] =
		disciplineApi.useShowSubjectMutationMutation()

	const [editSubject, { isLoading, isSuccess }] =
		disciplineApi.useEditSubjectMutation()

	const {
		register,
		handleSubmit,
		setValue,
		formState: { errors, touchedFields },
	} = useForm<Inputs>({
		mode: 'onBlur',
		resolver: yupResolver(addSubjectsSchema),
	})

	useEffect(() => {
		if (id && modalName === 'editSubject') {
			getSubject({
				id: Number(id),
			})
		}
	}, [id, modalName, getSubject])

	const onSubmit: SubmitHandler<Inputs> = (data) => {
		editSubject({
			name: data.name,
			disciplineId,
			id: id as number,
		})
	}

	useEffect(() => {
		if (isSuccess) {
			dispatch(closeModal())
			dispatch(notificationCreators.show(successMessages.updatedSubject))
		}
	}, [isSuccess, dispatch])

	useEffect(() => {
		if (subject) {
			setValue('name', subject?.name as string)
		}
	}, [subject, setValue])

	return (
		<Modal name='editSubject'>
			<Dialog.Panel
				style={getCssStyle(
					{ name: 'overflowY', value: 'auto' },
					{ name: 'maxWidth', value: pixelsToRem(630) },
					{ name: 'maxHeight', value: pixelsToRem(600) },
					{ name: 'width', value: '100%' }
				)}
				className={`${styles.container} modal-scroll`}
			>
				<p className={styles.title}>Editar Assunto</p>
				<p className={styles.subTitle}>
					Preencha os campos abaixo para editar o assunto
				</p>

				<form onSubmit={handleSubmit(onSubmit)}>
					<InputGroup className='mt-4' label='Nome do assunto' id='name'>
						<Input
							isValid={inputIsValid(errors.name, touchedFields.name)}
							{...register('name')}
							type='text'
						/>
					</InputGroup>

					<div className={styles.buttonGroup}>
						<Button
							type='button'
							onClick={() => {
								dispatch(closeModal())
							}}
							variant='outline'
							disabled={false}
						>
							Cancelar
						</Button>

						<Button type='submit' isLoading={isLoading} disabled={!modalName}>
							Editar Assunto
						</Button>
					</div>
				</form>
			</Dialog.Panel>
		</Modal>
	)
}
